import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "./captureException";
import { isTelemetryEnabled } from "./env";

type State = {
  hasError: boolean;
};

class LocalErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const DefaultErrorState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center">
      <p className="mb-4 text-lg font-medium">Something went wrong</p>
      <p className="text-sm text-gray-600">Please try again later</p>
    </div>
  </div>
);

export function TelemetryErrorBoundary({
  children,
  fallback = <DefaultErrorState />,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  if (!isTelemetryEnabled()) {
    return <LocalErrorBoundary fallback={fallback}>{children}</LocalErrorBoundary>;
  }

  return (
    <SentryErrorBoundary fallback={() => <>{fallback}</>}>
      {children}
    </SentryErrorBoundary>
  );
}
